import React, { useEffect, useRef } from 'react';
import LoadingSpinner from '../../LoadingSpinner/LoadingSpinner';

const formatGeneratedAt = (isoString) => {
	if (!isoString) return null;
	const date = new Date(isoString);
	if (Number.isNaN(date.getTime())) return null;
	return date.toLocaleDateString(undefined, {
		year: 'numeric',
		month: 'short',
		day: 'numeric'
	});
};

const getGradeColor = (grade) => {
	const letter =
		grade && grade.charAt(0) ? grade.charAt(0).toUpperCase() : null;
	switch (letter) {
		case 'A':
			return 'text-green-700 bg-green-50 border-green-300 dark:text-green-300 dark:bg-green-900 dark:bg-opacity-30 dark:border-green-700';
		case 'B':
			return 'text-blue-700 bg-blue-50 border-blue-300 dark:text-blue-300 dark:bg-blue-900 dark:bg-opacity-30 dark:border-blue-700';
		case 'C':
			return 'text-yellow-700 bg-yellow-50 border-yellow-300 dark:text-yellow-300 dark:bg-yellow-900 dark:bg-opacity-30 dark:border-yellow-700';
		case 'D':
			return 'text-orange-700 bg-orange-50 border-orange-300 dark:text-orange-300 dark:bg-orange-900 dark:bg-opacity-30 dark:border-orange-700';
		case 'F':
			return 'text-red-700 bg-red-50 border-red-300 dark:text-red-300 dark:bg-red-900 dark:bg-opacity-30 dark:border-red-700';
		default:
			return 'text-gray-700 bg-gray-50 border-gray-300 dark:text-dark-text dark:bg-dark-bg dark:border-dark-border';
	}
};

const getSignalColor = (signal) => {
	if (!signal) return 'text-gray-700 dark:text-dark-text-secondary';
	const normalized = signal.toLowerCase();
	if (normalized.includes('strong buy') || normalized.startsWith('buy')) {
		return 'text-green-700 dark:text-green-400';
	}
	if (normalized.includes('strong sell') || normalized.startsWith('sell')) {
		return 'text-red-700 dark:text-red-400';
	}
	return 'text-amber-700 dark:text-amber-400';
};

const PlayerAiInsightsModal = ({
	isLoading,
	isOpen,
	onClose,
	playerAiInsights
}) => {
	const modalRef = useRef(null);

	const handleClickOutsideModal = (event) => {
		if (modalRef.current && !modalRef.current.contains(event.target)) {
			onClose();
		}
	};

	useEffect(() => {
		document.addEventListener('mousedown', handleClickOutsideModal);
		return () => {
			document.removeEventListener('mousedown', handleClickOutsideModal);
		};
	}, []);

	useEffect(
		() => {
			if (isOpen) {
				document.body.style.overflow = 'hidden';
			} else {
				document.body.style.overflow = 'auto';
			}
		},
		[isOpen]
	);

	if (!isOpen) return null;

	const generatedLabel = formatGeneratedAt(playerAiInsights?.generated_at);
	const executiveSummary = playerAiInsights?.executive_summary;
	const playerAssessment = playerAiInsights?.player_assessment;
	const fantasyOutlook = playerAiInsights?.fantasy_outlook;
	const actionableTakeaways = playerAiInsights?.actionable_takeaways;
	const hasInsights =
		executiveSummary ||
		playerAssessment ||
		fantasyOutlook ||
		actionableTakeaways;

	return (
		<div
			className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 px-4"
			style={{ zIndex: 1000 }}
		>
			<div
				className="relative bg-white dark:bg-dark-card rounded-2xl border border-gray-200 dark:border-dark-border text-gray-800 dark:text-dark-text shadow-xl dark:shadow-2xl w-full max-w-3xl max-h-full overflow-hidden"
				ref={modalRef}
			>
				<div className="bg-ff-black text-white px-6 py-5">
					<div className="flex items-start justify-between gap-4 pr-8">
						<div>
							<h2 className="text-xl font-bold tracking-tight">
								Player AI Insights
							</h2>
							<p className="text-gray-300 text-sm mt-1">
								Strategic evaluation & fantasy outlook
							</p>
							{generatedLabel && (
								<p className="text-gray-400 text-xs mt-2">
									Analysis from {generatedLabel}
									{playerAiInsights?.cached === true && ' · cached'}
								</p>
							)}
						</div>
						<button
							type="button"
							className="absolute top-4 right-4 p-2 rounded-lg hover:bg-white hover:bg-opacity-10 transition duration-200"
							onClick={onClose}
							aria-label="Close"
						>
							<svg
								className="h-5 w-5"
								fill="none"
								viewBox="0 0 24 24"
								stroke="currentColor"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth="2"
									d="M6 18L18 6M6 6l12 12"
								/>
							</svg>
						</button>
					</div>
				</div>

				<div
					className="overflow-y-auto p-5 bg-gray-50 dark:bg-dark-bg space-y-5"
					style={{ maxHeight: 'calc(90vh - 100px)' }}
				>
					{isLoading ? (
						<div className="flex justify-center py-12">
							<LoadingSpinner />
						</div>
					) : hasInsights ? (
						<>
							{executiveSummary && (
								<div className="bg-white dark:bg-dark-card rounded-xl shadow-sm border border-gray-200 dark:border-dark-border overflow-hidden">
									<div className="px-5 py-3 border-b border-gray-200 dark:border-dark-border">
										<h3 className="text-base font-bold text-gray-900 dark:text-dark-text">
											Executive Summary
										</h3>
									</div>
									<div className="p-5 space-y-4">
										{executiveSummary.overall_grade && (
											<div className="flex items-center justify-between">
												<span className="font-semibold text-gray-700 dark:text-gray-300">
													Overall Grade
												</span>
												<span
													className={`px-4 py-2 rounded-lg border-2 font-bold text-lg ${getGradeColor(
														executiveSummary.overall_grade
													)}`}
												>
													{executiveSummary.overall_grade}
												</span>
											</div>
										)}
										{executiveSummary.grade_rationale && (
											<p className="text-gray-800 dark:text-gray-200 leading-relaxed text-sm bg-gray-50 dark:bg-dark-bg p-4 rounded-lg border border-gray-200 dark:border-dark-border">
												{executiveSummary.grade_rationale}
											</p>
										)}
										{executiveSummary.player_archetype && (
											<div className="bg-blue-50 dark:bg-blue-900 dark:bg-opacity-20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
												<p className="text-xs font-bold text-blue-700 dark:text-blue-300 uppercase tracking-wider mb-1">
													Archetype
												</p>
												<p className="text-gray-800 dark:text-gray-200 text-sm leading-relaxed">
													{executiveSummary.player_archetype}
												</p>
											</div>
										)}
									</div>
								</div>
							)}

							{playerAssessment && (
								<div className="bg-white dark:bg-dark-card rounded-xl shadow-sm border border-gray-200 dark:border-dark-border overflow-hidden">
									<div className="px-5 py-3 border-b border-gray-200 dark:border-dark-border">
										<h3 className="text-base font-bold text-gray-900 dark:text-dark-text">
											Player Assessment
										</h3>
									</div>
									<div className="p-5 space-y-5">
										{playerAssessment.core_strengths?.length > 0 && (
											<div>
												<h4 className="text-xs font-bold text-green-700 dark:text-green-400 uppercase tracking-wider mb-2">
													Core Strengths
												</h4>
												<ul className="space-y-2">
													{playerAssessment.core_strengths.map(
														(item, index) => (
															<li
																key={index}
																className="text-sm leading-relaxed bg-green-50 dark:bg-green-900 dark:bg-opacity-20 border-l-4 border-green-500 pl-3 py-2 rounded-r-md text-gray-800 dark:text-gray-200"
															>
																{item}
															</li>
														)
													)}
												</ul>
											</div>
										)}
										{playerAssessment.key_risks?.length > 0 && (
											<div>
												<h4 className="text-xs font-bold text-red-700 dark:text-red-400 uppercase tracking-wider mb-2">
													Key Risks
												</h4>
												<ul className="space-y-2">
													{playerAssessment.key_risks.map((item, index) => (
														<li
															key={index}
															className="text-sm leading-relaxed bg-red-50 dark:bg-red-900 dark:bg-opacity-20 border-l-4 border-red-500 pl-3 py-2 rounded-r-md text-gray-800 dark:text-gray-200"
														>
															{item}
														</li>
													))}
												</ul>
											</div>
										)}
									</div>
								</div>
							)}

							{fantasyOutlook && (
								<div className="bg-white dark:bg-dark-card rounded-xl shadow-sm border border-gray-200 dark:border-dark-border overflow-hidden">
									<div className="px-5 py-3 border-b border-gray-200 dark:border-dark-border">
										<h3 className="text-base font-bold text-gray-900 dark:text-dark-text">
											Fantasy Outlook
										</h3>
									</div>
									<div className="p-5 space-y-4 text-sm leading-relaxed">
										{fantasyOutlook.redraft_value && (
											<div>
												<p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
													Redraft
												</p>
												<p className="text-gray-800 dark:text-gray-200">
													{fantasyOutlook.redraft_value}
												</p>
											</div>
										)}
										{fantasyOutlook.dynasty_value && (
											<div>
												<p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
													Dynasty
												</p>
												<p className="text-gray-800 dark:text-gray-200">
													{fantasyOutlook.dynasty_value}
												</p>
											</div>
										)}
										{fantasyOutlook.format_notes && (
											<div className="bg-gray-50 dark:bg-dark-bg p-4 rounded-lg border border-gray-200 dark:border-dark-border">
												<p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
													Format Fit
												</p>
												<p className="text-gray-800 dark:text-gray-200">
													{fantasyOutlook.format_notes}
												</p>
											</div>
										)}
									</div>
								</div>
							)}

							{actionableTakeaways && (
								<div className="bg-white dark:bg-dark-card rounded-xl shadow-sm border border-gray-200 dark:border-dark-border overflow-hidden">
									<div className="px-5 py-3 border-b border-gray-200 dark:border-dark-border">
										<h3 className="text-base font-bold text-gray-900 dark:text-dark-text">
											Actionable Takeaways
										</h3>
									</div>
									<div className="p-5 space-y-4 text-sm">
										{actionableTakeaways.primary && (
											<div>
												<p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
													Priority
												</p>
												<p className="text-gray-800 dark:text-gray-200 leading-relaxed">
													{actionableTakeaways.primary}
												</p>
											</div>
										)}
										{actionableTakeaways.secondary && (
											<div>
												<p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
													Also Consider
												</p>
												<p className="text-gray-800 dark:text-gray-200 leading-relaxed">
													{actionableTakeaways.secondary}
												</p>
											</div>
										)}
										{actionableTakeaways.acquisition_signal && (
											<div className="pt-2 border-t border-gray-200 dark:border-dark-border">
												<p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
													Acquisition Signal
												</p>
												<p
													className={`font-semibold leading-relaxed ${getSignalColor(
														actionableTakeaways.acquisition_signal
													)}`}
												>
													{actionableTakeaways.acquisition_signal}
												</p>
											</div>
										)}
									</div>
								</div>
							)}
						</>
					) : (
						<p className="text-gray-500 dark:text-dark-text-secondary text-center py-8">
							No insights available.
						</p>
					)}
				</div>
			</div>
		</div>
	);
};

export default PlayerAiInsightsModal;
